import React, { useEffect, useState } from 'react';
import { apiGet } from '../../contexts/AuthContext';
import { useAuth } from '../../contexts/AuthContext';
import { StatusBadge } from '../../components/WorkflowTracker';
import CountUp from '../../components/CountUp';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import LoadError from '../../components/LoadError';
import { motion } from 'framer-motion';
import {
  Users, Building2, UserCircle, TrendingUp, ArrowRight, Globe, X,
  GraduationCap, ShieldCheck, FileText, Search, Award, Plane,
} from 'lucide-react';

const COUNTRY_FLAGS = {
  'US': '🇺🇸', 'USA': '🇺🇸', 'United States': '🇺🇸',
  'UK': '🇬🇧', 'United Kingdom': '🇬🇧',
  'Canada': '🇨🇦',
  'Australia': '🇦🇺',
  'Germany': '🇩🇪',
  'France': '🇫🇷',
  'Ireland': '🇮🇪',
  'New Zealand': '🇳🇿',
  'Singapore': '🇸🇬',
  'India': '🇮🇳',
  'Italy': '🇮🇹',
  'Spain': '🇪🇸',
  'Netherlands': '🇳🇱',
  'Sweden': '🇸🇪',
  'Japan': '🇯🇵',
  'South Korea': '🇰🇷',
  'Switzerland': '🇨🇭',
  'Dubai': '🇦🇪', 'UAE': '🇦🇪', 'United Arab Emirates': '🇦🇪',
  'Malaysia': '🇲🇾',
  'China': '🇨🇳',
};

const COUNTRY_COLORS = ['rgb(var(--xp-brand))', 'rgb(var(--xp-ember))'];

const QUICK_COUNTRIES = [
  'USA', 'UK', 'Australia', 'Canada', 'Germany', 'France', 'Ireland',
  'New Zealand', 'Singapore', 'Malaysia', 'Dubai', 'Netherlands',
  'Switzerland', 'Italy', 'Spain', 'Poland', 'Latvia',
];

const containerStagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06 } },
};
const itemUp = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] } },
};

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [countryCounts, setCountryCounts] = useState({});
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [countryCandidates, setCountryCandidates] = useState([]);
  const [countryLoading, setCountryLoading] = useState(false);
  const [uniCountries, setUniCountries] = useState([]);

  useEffect(() => {
    setLoading(true);
    setLoadError(false);
    Promise.all([
      apiGet('/admin/stats'),
      apiGet('/admin/country-stats'),
    ]).then(([statsRes, countryRes]) => {
      setStats(statsRes.data);
      setCountryCounts(countryRes.data.country_counts || {});
    }).catch(() => setLoadError(true)).finally(() => setLoading(false));
    apiGet('/universities/countries').then(r => setUniCountries(r.data || [])).catch(console.error);
  }, [attempt]);

  const handleCountryClick = (country) => {
    if (selectedCountry === country) {
      setSelectedCountry(null);
      setCountryCandidates([]);
      return;
    }
    setSelectedCountry(country);
    setCountryLoading(true);
    apiGet(`/admin/candidates-by-country?country=${encodeURIComponent(country)}`)
      .then(r => setCountryCandidates(r.data))
      .catch(() => { toast.error(`Couldn't load candidates for ${country}`); setSelectedCountry(null); })
      .finally(() => setCountryLoading(false));
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-3 border-brand border-t-transparent rounded-full animate-spin" /></div>;
  // Without this, a failed load showed 0 partners / 0 candidates as if the data were gone.
  if (loadError) return <div className="space-y-8" data-testid="admin-dashboard"><LoadError what="the dashboard" onRetry={() => setAttempt(a => a + 1)} /></div>;

  const statCards = [
    { label: 'Total Partners', value: stats?.total_partners || 0, icon: UserCircle, gradient: 'linear-gradient(135deg, #0044CC 0%, #003399 100%)', to: '/admin/partners' },
    { label: 'Total Candidates', value: stats?.total_candidates || 0, icon: Users, gradient: 'linear-gradient(135deg, #D85A00 0%, #B84D00 100%)', to: '/admin/candidates' },
    { label: 'Universities', icon: Building2, gradient: 'linear-gradient(135deg, #0055FF 0%, #0044CC 100%)', to: '/admin/universities', hideCount: true },
    { label: 'Enrolled', value: stats?.status_counts?.enrolled || 0, icon: TrendingUp, gradient: 'linear-gradient(135deg, #FF7B1A 0%, #D85A00 100%)', to: '/admin/candidates?status=enrolled' },
  ];

  const countryEntries = Object.entries(countryCounts);

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div className="flex items-end justify-between gap-5 flex-wrap">
        <div><p className="passport-eyebrow">XpertAdvisor / Admissions workspace</p><h1 className="passport-heading mt-3">Every student. Every next step.</h1><p className="text-ink-2 text-sm mt-3">Review partner handoffs, coordinate applications, and keep the journey moving.</p></div>
        <Link to="/admin/applications" className="passport-action">Review applications <ArrowRight size={16}/></Link>
      </div>

      {/* Quick country tiles */}
      {uniCountries.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="university-country-tiles-section">
          {[...QUICK_COUNTRIES.filter(qc => uniCountries.some(c => c.toLowerCase() === qc.toLowerCase())), ...uniCountries.filter(c => c && !QUICK_COUNTRIES.some(qc => qc.toLowerCase() === c.toLowerCase()))].map(qc => (
            <Link
              key={qc}
              to={`/admin/universities?country=${encodeURIComponent(qc)}`}
              data-testid={`uni-country-tile-${qc.toLowerCase().replace(/\s/g, '-')}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border border-contrast/10 bg-surface text-ember hover:border-brand/50 hover:bg-brand/[0.06] hover:text-ember transition-all shadow-sm"
            >
              <span>{qc}</span>
            </Link>
          ))}
        </div>
      )}

      {/* Stat Cards */}
      <motion.div
        variants={containerStagger}
        initial="hidden"
        animate="show"
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
      >
        {statCards.map((card) => {
          const Icon = card.icon;
          return (
            <motion.div key={card.label} variants={itemUp} whileHover={{ y: -4 }} className="h-full">
              <Link
                to={card.to}
                data-testid={`stat-card-${card.label.toLowerCase().replace(/\s/g, '-')}`}
                className="relative overflow-hidden rounded-2xl p-6 text-white hover:shadow-xl transition-shadow group flex flex-col justify-between h-full"
                style={{ background: card.gradient }}
              >
                <div className="flex items-start justify-between">
                  {card.hideCount ? (
                    <p className="text-2xl font-bold mt-3 tracking-tight">{card.label}</p>
                  ) : (
                    <div>
                      <p className="text-white/70 text-sm font-medium">{card.label}</p>
                      <p className="text-4xl font-bold mt-1 tracking-tight"><CountUp value={card.value} /></p>
                    </div>
                  )}
                  <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="flex items-center gap-1 mt-4 text-white/70 text-xs font-medium group-hover:text-white transition-colors">
                  <ArrowRight className="w-3.5 h-3.5" /> View details
                </div>
                <div className="absolute -bottom-4 -right-4 w-24 h-24 rounded-full bg-white/10" />
                <div className="absolute -bottom-8 -right-8 w-32 h-32 rounded-full bg-white/5" />
              </Link>
            </motion.div>
          );
        })}
      </motion.div>

      {/* Country Tiles */}
      <div className="glass-card-dark p-6" data-testid="country-tiles-section">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-brand/20 border border-brand/30 flex items-center justify-center">
            <Globe className="w-5 h-5 text-brand-soft" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-ink" style={{ fontFamily: 'Outfit' }}>Candidates by Country</h2>
            <p className="text-xs text-ink-2">Click a country to view its candidates</p>
          </div>
        </div>
        {countryEntries.length > 0 ? (
          <motion.div
            variants={containerStagger}
            initial="hidden"
            animate="show"
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4"
          >
            {countryEntries.map(([country, count], idx) => {
              const color = COUNTRY_COLORS[idx % COUNTRY_COLORS.length];
              const flag = COUNTRY_FLAGS[country] || '🌍';
              const isActive = selectedCountry === country;
              return (
                <motion.button
                  key={country}
                  variants={itemUp}
                  whileHover={{ y: -3 }}
                  onClick={() => handleCountryClick(country)}
                  data-testid={`country-tile-${country.toLowerCase().replace(/\s/g, '-')}`}
                  className={`relative p-5 rounded-xl border text-left transition-all hover:shadow-md cursor-pointer bg-surface-2/70 ${
                    isActive
                      ? 'border-brand shadow-lift bg-brand/[0.06]'
                      : 'border-contrast/10 hover:border-brand/40 hover:bg-surface hover:shadow-card'
                  }`}
                >
                  <div className="text-3xl mb-2">{flag}</div>
                  <p className="text-sm font-semibold text-ink truncate">{country}</p>
                  <p className="text-2xl font-bold mt-1" style={{ color }}><CountUp value={count} /></p>
                  <p className="text-xs text-ink-2">{count === 1 ? 'candidate' : 'candidates'}</p>
                  {isActive && (
                    <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-brand flex items-center justify-center">
                      <span className="block w-2 h-2 rounded-full bg-white" />
                    </div>
                  )}
                </motion.button>
              );
            })}
          </motion.div>
        ) : (
          <p className="text-ink-3 text-sm">No candidates with country data yet</p>
        )}
      </div>

      {/* Country Drilldown Table */}
      {selectedCountry && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="glass-card-dark p-6"
          data-testid="country-drilldown"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <span className="text-2xl">{COUNTRY_FLAGS[selectedCountry] || '🌍'}</span>
              <div>
                <h2 className="text-lg font-semibold text-ink" style={{ fontFamily: 'Outfit' }}>
                  {selectedCountry} — Candidates
                </h2>
                <p className="text-xs text-ink-2">{countryCandidates.length} {countryCandidates.length === 1 ? 'candidate' : 'candidates'} found</p>
              </div>
            </div>
            <button
              onClick={() => { setSelectedCountry(null); setCountryCandidates([]); }}
              className="p-2 rounded-lg hover:bg-contrast/[0.06] text-ink-2 hover:text-ink transition-colors"
              data-testid="close-drilldown"
              aria-label="Close country list"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          {countryLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="w-6 h-6 border-2 border-brand border-t-transparent rounded-full animate-spin" />
            </div>
          ) : countryCandidates.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="country-candidates-table">
                <thead>
                  <tr className="border-b border-contrast/10">
                    <th className="text-left py-3 px-4 font-medium text-ink-2">XpertID</th>
                    <th className="text-left py-3 px-4 font-medium text-ink-2">Candidate Name</th>
                    <th className="text-left py-3 px-4 font-medium text-ink-2">Partner Name</th>
                    <th className="text-left py-3 px-4 font-medium text-ink-2">University</th>
                    <th className="text-left py-3 px-4 font-medium text-ink-2">Intake</th>
                    <th className="text-left py-3 px-4 font-medium text-ink-2">Course</th>
                    <th className="text-left py-3 px-4 font-medium text-ink-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {countryCandidates.map((c) => (
                    <tr key={c.id} className="border-b border-contrast/[0.06] hover:bg-surface-2 transition-colors">
                      <td className="py-3 px-4">
                        <span className="text-xs font-mono font-semibold text-brand-soft bg-brand/20 border border-brand/30 px-2 py-0.5 rounded">{c.xpert_id || 'N/A'}</span>
                      </td>
                      <td className="py-3 px-4">
                        <Link to={`/admin/candidates/${c.id}`} className="text-ink hover:text-brand-soft font-medium">
                          {c.name || 'N/A'}
                        </Link>
                      </td>
                      <td className="py-3 px-4 text-ink-2">{c.partner_name || 'N/A'}</td>
                      <td className="py-3 px-4 text-ink-2">{c.university_name || 'N/A'}</td>
                      <td className="py-3 px-4 text-ink-2">{c.semester_intake_year || 'N/A'}</td>
                      <td className="py-3 px-4 text-ink-2">{c.course_interested || 'N/A'}</td>
                      <td className="py-3 px-4"><StatusBadge status={c.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-ink-2 text-sm">No candidates found for {selectedCountry}</p>
          )}
        </motion.div>
      )}

      {/* Status Pipeline */}
      <div className="glass-card-dark p-6">
        <h2 className="text-lg font-semibold text-ink mb-5" style={{ fontFamily: 'Outfit' }}>Candidate Journey</h2>
        <motion.div
          variants={containerStagger}
          initial="hidden"
          animate="show"
          className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3"
        >
          {Object.entries(stats?.status_counts || {}).map(([status, count]) => (
            <motion.div key={status} variants={itemUp} className="h-full flex flex-col items-center justify-between text-center p-4 rounded-xl bg-surface-2/70 border border-contrast/10 hover:border-brand/40 hover:bg-surface hover:shadow-card transition-all hover:-translate-y-1" data-testid={`pipeline-${status}`}>
              <StatusBadge status={status} />
              <p className="text-3xl font-bold text-ink mt-2"><CountUp value={count} /></p>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Recent Candidates */}
      <div className="glass-card-dark p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-ink" style={{ fontFamily: 'Outfit' }}>Recent Candidates</h2>
          <Link to="/admin/candidates" className="text-sm text-brand-soft hover:text-brand font-medium" data-testid="view-all-candidates">View All</Link>
        </div>
        {stats?.recent_candidates?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="recent-candidates-table">
              <thead>
                <tr className="border-b border-contrast/10">
                  <th className="text-left py-3 px-4 font-medium text-ink-2">Name</th>
                  <th className="text-left py-3 px-4 font-medium text-ink-2">Partner</th>
                  <th className="text-left py-3 px-4 font-medium text-ink-2">Country</th>
                  <th className="text-left py-3 px-4 font-medium text-ink-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent_candidates.map((c) => (
                  <tr key={c.id} className="border-b border-contrast/[0.06] hover:bg-surface-2 transition-colors">
                    <td className="py-3 px-4">
                      <Link to={`/admin/candidates/${c.id}`} className="text-ink hover:text-brand-soft font-medium">
                        {c.name || `${c.first_name || ''} ${c.last_name || ''}`}
                      </Link>
                    </td>
                    <td className="py-3 px-4 text-ink-2">{c.partner_name || 'N/A'}</td>
                    <td className="py-3 px-4 text-ink-2">{c.country || c.target_country || 'N/A'}</td>
                    <td className="py-3 px-4"><StatusBadge status={c.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-ink-2 text-sm">No candidates yet</p>
        )}
      </div>
    </div>
  );
}
